import React, { useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
} from '@chakra-ui/react'
import { FiZap, FiCheck } from 'react-icons/fi'
import { FileItem } from '@/types'
import { useSmartTagSuggestions } from '@/hooks'
import {
  FileInfoDisplay,
  SimilarDocumentsTab,
  ContentAnalysisTab,
} from '@/components/TagSuggestions'
import { useAppTranslation } from '@/contexts/LanguageContext'

interface SmartTagSuggestionsModalProps {
  isOpen: boolean
  onClose: () => void
  file: FileItem
  existingTags: string[]
  onTagsAdded: (addedTags: string[]) => void
}

export default function SmartTagSuggestionsModal({
  isOpen,
  onClose,
  file,
  existingTags,
  onTagsAdded,
}: SmartTagSuggestionsModalProps) {
  const [activeTab, setActiveTab] = useState<'similar' | 'content'>('similar')
  const { t } = useAppTranslation('files')

  const {
    // Selection state
    selectionCount,
    toggleTag,
    isSelected,
    clearSelection,

    // Recommendations
    recommendations,
    recommendationsLoading,
    recommendationsError,
    method,
    handleMethodChange,

    // Content suggestions
    contentSuggestions,
    contentLoading,
    contentError,
    contentOptions,
    setContentOptions,
    handleContentAnalyze,

    // Actions
    handleAddSelectedTags,
    isAdding,
  } = useSmartTagSuggestions({
    file,
    existingTags,
    isOpen,
    onTagsAdded,
    onClose,
  })

  const handleTabChange = (index: number) => {
    const newTab = index === 0 ? 'similar' : 'content'
    setActiveTab(newTab)
    clearSelection() // Clear selection when switching tabs
  }

  const handleContentOptionsChange = (field: keyof typeof contentOptions, value: any) => {
    setContentOptions({ [field]: value })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="lg" boxShadow="xl" maxH="90vh" overflow="hidden">
        <ModalHeader
          bgGradient="linear(to-r, blue.500, purple.600)"
          color="white"
          py={6}
          borderRadius="lg lg 0 0"
        >
          <HStack spacing={3}>
            <FiZap size="24px" />
            <VStack align="start" spacing={1}>
              <Text fontSize="xl" fontWeight="bold">
                {t('smartTagSuggestionsModal.title')}
              </Text>
              <HStack spacing={2}>
                <Badge
                  bg="whiteAlpha.300"
                  color="white"
                  variant="solid"
                  borderRadius="full"
                  px={3}
                  py={1}
                  fontSize="xs"
                >
                  {t('smartTagSuggestionsModal.aiPowered')}
                </Badge>
                <Badge
                  bg="whiteAlpha.300"
                  color="white"
                  variant="solid"
                  borderRadius="full"
                  px={3}
                  py={1}
                  fontSize="xs"
                >
                  {method.replace('_', ' ').toUpperCase()}
                </Badge>
              </HStack>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton color="white" size="lg" top={4} right={4} />

        <ModalBody p={6} bg="gray.50" overflowY="auto">
          <VStack spacing={6} align="stretch">
            {/* File Info */}
            <FileInfoDisplay file={file} />

            <Tabs
              index={activeTab === 'similar' ? 0 : 1}
              onChange={handleTabChange}
              variant="soft-rounded"
            >
              <TabList bg="white" borderRadius="lg" p={1} border="1px solid" borderColor="gray.200">
                <Tab
                  borderRadius="md"
                  _selected={{
                    bg: 'blue.500',
                    color: 'white',
                    boxShadow: 'sm',
                  }}
                  _hover={{
                    bg: activeTab === 'similar' ? 'blue.600' : 'gray.100',
                  }}
                  transition="all 0.2s"
                >
                  <HStack spacing={2}>
                    <Text fontWeight="medium">
                      {t('smartTagSuggestionsModal.similarDocuments')}
                    </Text>
                    <Badge
                      colorScheme={activeTab === 'similar' ? 'whiteAlpha' : 'blue'}
                      variant="solid"
                      borderRadius="full"
                    >
                      {recommendations.length}
                    </Badge>
                  </HStack>
                </Tab>
                <Tab
                  borderRadius="md"
                  _selected={{
                    bg: 'blue.500',
                    color: 'white',
                    boxShadow: 'sm',
                  }}
                  _hover={{
                    bg: activeTab === 'content' ? 'blue.600' : 'gray.100',
                  }}
                  transition="all 0.2s"
                >
                  <HStack spacing={2}>
                    <Text fontWeight="medium">{t('smartTagSuggestionsModal.contentAnalysis')}</Text>
                    <Badge
                      colorScheme={activeTab === 'content' ? 'whiteAlpha' : 'blue'}
                      variant="solid"
                      borderRadius="full"
                    >
                      {contentSuggestions.length}
                    </Badge>
                  </HStack>
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel px={0}>
                  <SimilarDocumentsTab
                    recommendations={recommendations}
                    isLoading={recommendationsLoading}
                    error={recommendationsError}
                    method={method}
                    onMethodChange={handleMethodChange}
                    selectionCount={selectionCount}
                    onToggleTag={toggleTag}
                    isTagSelected={isSelected}
                  />
                </TabPanel>

                <TabPanel px={0}>
                  <ContentAnalysisTab
                    suggestions={contentSuggestions}
                    isLoading={contentLoading}
                    error={contentError}
                    options={contentOptions}
                    onOptionsChange={handleContentOptionsChange}
                    onAnalyze={handleContentAnalyze}
                    selectionCount={selectionCount}
                    onToggleTag={toggleTag}
                    isTagSelected={isSelected}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </VStack>
        </ModalBody>

        <ModalFooter bg="white" borderTop="1px solid" borderColor="gray.200" py={4}>
          <HStack spacing={3} w="full" justify="space-between">
            <Text fontSize="sm" color="gray.600">
              {t('smartTagSuggestionsModal.tagsSelected', { count: selectionCount })}
            </Text>
            <HStack spacing={3}>
              <Button
                variant="ghost"
                onClick={onClose}
                isDisabled={isAdding}
                borderRadius="lg"
                px={6}
                _hover={{ bg: 'gray.100' }}
              >
                {t('smartTagSuggestionsModal.cancel')}
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleAddSelectedTags}
                isDisabled={selectionCount === 0 || isAdding}
                leftIcon={isAdding ? <Spinner size="xs" /> : <FiCheck />}
                borderRadius="lg"
                px={6}
                py={3}
                h="auto"
                fontWeight="semibold"
                _hover={{ transform: 'translateY(-1px)', boxShadow: 'lg' }}
                transition="all 0.2s"
              >
                {selectionCount > 0
                  ? t('smartTagSuggestionsModal.addSelectedTagsWithCount', {
                      count: selectionCount,
                    })
                  : t('smartTagSuggestionsModal.addSelectedTags')}
              </Button>
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
